import React from 'react';
import {
  Form, FormActionsAlign, FormHandlers, FormProps, FormSubmitType,
  Icon,
} from '@doveit/bricks';
import { omit } from 'lodash';
import { CreateJobApplicationFormModel } from '../../types';
import { Department, JobApplicationType } from '../../../domain/types';
import { jobApplicationTypeLabels } from '../../../labels';
import JobPositionsSelect from '../../../job-position/containers/job-positions-select/JobPositionsSelect';
import { booleanStringToBool } from '../../../utils/boolean-string/booleanString';
import { INPUT_PLACEHOLDERS } from '../../../constants';
import { ThemeIcons } from '../../../theme/icons';
import { JobPosition } from '../../../providers/api/dtos';
import { createJobApplicationFormSchema } from './CreateJobApplicationForm.schema';
import { jobApplicationSourceLabels } from '../../../labels/jobApplicationSourceLabels';
import { JobApplicationSource } from '../../../domain/types/jobApplicationSource';
import { asSelectOptions } from '../../../select-options/utils/asSelectOptions';
import { provinceSelectOptions } from '../../../select-options';
import { CreateJobApplicationFormSchemaOptions } from './types';
import { jobApplicationExperiencesRadioOptions } from '../../constants';

export interface CreateJobApplicationFormProps {
  initialValues: Partial<CreateJobApplicationFormModel>
  onSubmit: FormSubmitType<CreateJobApplicationFormModel>
  innerRef?: React.MutableRefObject<FormHandlers>,
  validationSchema?: (options: CreateJobApplicationFormSchemaOptions) => FormProps<CreateJobApplicationFormModel>['validationSchema'],
  hiddenFields?: Partial<Record<keyof CreateJobApplicationFormModel, boolean>>
  disabledFields?: Partial<Record<keyof CreateJobApplicationFormModel, boolean>>
  loading?: boolean,
  disabled?: boolean,
}

const CREATE_JOB_APPLICATION_FORM_DEFAULT_VALUES: CreateJobApplicationFormModel = {
  name: '',
  phoneNumber: '',
  email: '',
  linkedIn: '',
  applicationType: undefined,
  jobPosition: undefined,
  source: '',
  experience: '',
  isEmployed: undefined,
  provinces: [],
  hasLicense: undefined,
  licenseId: '',
  note: '',
};

const CreateJobApplicationForm: React.FC<CreateJobApplicationFormProps> = ({
  initialValues,
  innerRef,
  hiddenFields,
  disabledFields,
  validationSchema,
  ...rest
}) => {
  const mergedInitialValues = React.useMemo(() => ({
    ...CREATE_JOB_APPLICATION_FORM_DEFAULT_VALUES,
    ...initialValues,
  }), [initialValues]);

  const jobApplicationsSourceOptions = React.useMemo(() => {
    const filtered = omit(jobApplicationSourceLabels, JobApplicationSource.REFERRAL_AGENZIA);
    const sourceOptions = asSelectOptions(filtered, false).sort((a, b) => a.label.localeCompare(b.label, 'it'));
    return [{ label: 'Seleziona una sorgente', value: '', disabled: true }, ...sourceOptions];
  }, []);

  return (
    <Form
      {...rest}
      initialValues={mergedInitialValues}
      innerRef={innerRef}
      validationSchema={
        validationSchema
          ? validationSchema({ skipSource: disabledFields?.source })
          : createJobApplicationFormSchema({ skipSource: disabledFields?.source })
      }
    >
      {({ values, setFieldValue }) => {
        const onApplicationTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          setFieldValue('applicationType', e.target.value || undefined);
          setFieldValue('jobPosition', undefined);
        };

        const onRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          const newJobPosition = e.target.value as any as JobPosition;
          setFieldValue('jobPosition', newJobPosition);
        };

        return (
          <>
            <Form.Fieldset legend="Candidato">
              {!hiddenFields?.name && (
                <Form.Item>
                  <Form.Input
                    name="name"
                    label="Nome e cognome"
                    placeholder="Es. Mario Rossi"
                    aria-label="Campo per inserire il nome e il cognome"
                    required
                    disabled={disabledFields?.name}
                  />
                </Form.Item>
              )}
              {!hiddenFields?.email && (
                <Form.Item size={{ MD: 1 / 2 }}>
                  <Form.Input
                    name="email"
                    label="Email"
                    placeholder="Es. mario.rossi@gmail.com"
                    aria-label="Campo per inserire l'indirizzo email"
                    disabled={disabledFields?.email}
                  />
                </Form.Item>
              )}
              {!hiddenFields?.phoneNumber && (
                <Form.Item size={{ MD: 1 / 2 }}>
                  <Form.Input
                    name="phoneNumber"
                    label="Numero di telefono"
                    placeholder="Es. 3923456789"
                    aria-label="Campo per inserire il numero di telefono"
                    disabled={disabledFields?.phoneNumber}
                  />
                </Form.Item>
              )}
              {!hiddenFields?.linkedIn && (
                <Form.Item>
                  <Form.Input
                    name="linkedIn"
                    label="Profilo LinkedIn"
                    placeholder="Es. https://www.linkedin.com/in/mariorossi"
                    prefixElement={<Icon path={ThemeIcons.LINKEDIN} />}
                    aria-label="Campo per inserire il profilo LinkedIn"
                    disabled={disabledFields?.linkedIn}
                  />
                </Form.Item>
              )}
            </Form.Fieldset>
            <Form.Fieldset legend="Candidatura">
              {!hiddenFields?.applicationType && (
                <Form.Item size={{ MD: 1 / 2 }}>
                  <Form.Select
                    label="Tipologia"
                    name="applicationType"
                    required
                    aria-label="Campo per specificare la tipologia"
                    options={[
                      {
                        label: 'Seleziona',
                        value: '',
                      },
                      ...Object.values(JobApplicationType).map((value) => ({ value, label: jobApplicationTypeLabels[value] })),
                    ]}
                    disabled={disabledFields?.applicationType}
                    onChange={onApplicationTypeChange}
                  />
                </Form.Item>
              )}
              {!hiddenFields?.jobPosition && (
                <Form.Item size={{ MD: 1 / 2 }}>
                  <JobPositionsSelect
                    label="Posizione"
                    name="jobPosition"
                    value={values.jobPosition}
                    required
                    aria-label="Campo per specificare il ruolo"
                    filterJobPositions={(jobPosition) => (values.applicationType === JobApplicationType.SALES ? jobPosition.department === Department.SALES : jobPosition.department !== Department.SALES)}
                    disabled={disabledFields?.jobPosition}
                    onChange={onRoleChange}
                  />
                </Form.Item>
              )}
              {!hiddenFields?.isEmployed && (
                <Form.Item size={{ MD: 1 / 2 }}>
                  <Form.YesOrNotButtons
                    label="Attualmente lavora?"
                    name="isEmployed"
                    aria-label="Campo per specificare se il candidato attualmente lavora"
                    disabled={disabledFields?.isEmployed}
                  />
                </Form.Item>
              )}
              {!hiddenFields?.source && (
                <Form.Item size={{ MD: 1 / 2 }}>
                  <Form.Select
                    label="Sorgente"
                    name="source"
                    required
                    aria-label="Campo per specificare la sorgente"
                    options={jobApplicationsSourceOptions}
                    disabled={disabledFields?.source}
                  />
                </Form.Item>
              )}
              {!hiddenFields?.experience && (
                <Form.Item>
                  <Form.RadioButtonGroup
                    name="experience"
                    label="Livello di esperienza"
                    itemSize={{ MD: 1 / 2, LG: 1 / 4 }}
                    items={jobApplicationExperiencesRadioOptions}
                    aria-label="Campo per specificare l'esperienza"
                    disabled={disabledFields?.experience}
                  />
                </Form.Item>
              )}
              {values.applicationType === JobApplicationType.SALES && (
                <>
                  {!hiddenFields?.provinces && (
                    <Form.Item>
                      <Form.MultiSelect
                        name="provinces"
                        label="Luogo"
                        options={provinceSelectOptions}
                        required
                        aria-label="Campo per specificare le province coperte"
                        disabled={disabledFields?.provinces}
                      />
                    </Form.Item>
                  )}
                  {!hiddenFields?.hasLicense && (
                    <Form.Item size={{ MD: 1 / 2 }}>
                      <Form.YesOrNotButtons
                        label="Ha il patentino?"
                        name="hasLicense"
                        required
                        aria-label="Campo per specificare se il candidato ha il patentino"
                        disabled={disabledFields?.hasLicense}
                      />
                    </Form.Item>
                  )}
                  {!hiddenFields?.licenseId && booleanStringToBool(values.hasLicense) && (
                    <Form.Item size={{ MD: 1 / 2 }}>
                      <Form.Input
                        label="Codice patentino"
                        placeholder={INPUT_PLACEHOLDERS.JOB_APPLICATION_LICENSE_ID}
                        name="licenseId"
                        aria-label="Campo per specificare il codice del patentino"
                        disabled={disabledFields?.licenseId}
                      />
                    </Form.Item>
                  )}
                </>
              )}
            </Form.Fieldset>
            {!hiddenFields?.note && (
              <Form.Item>
                <Form.TextArea
                  name="note"
                  label="Note"
                  aria-label="Campo per inserire le note della candidatura"
                  disabled={disabledFields?.note}
                />
              </Form.Item>
            )}
            {!innerRef && (
              <Form.Actions align={FormActionsAlign.RIGHT}>
                <Form.Submit
                  label="Aggiungi"
                  aria-label="Aggiungi la candidatura"
                />
              </Form.Actions>
            )}
          </>
        );
      }}
    </Form>
  );
};

export default CreateJobApplicationForm;
